<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { useTranslation } from 'i18next-vue'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import { useFieldError } from '@/composables/useFieldError'
import { normalizeExternalUrl } from '@/api/url'
import { getExternalUrlError } from '@/validation/schemas/common'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    // WHY: Optional SectionedForm field name, so schema errors (e.g. on "next") are shown here too.
    field?: string
    // WHY: Error from a form validation outside SectionedForm (e.g. useFormValidation), already translated.
    error?: string
    fluid?: boolean
}>()

const model = defineModel<string | null | undefined>()

const { t } = useTranslation()

const formField = props.field ? useFieldError(props.field) : null
const touched = ref(false)

// WHY: Validate only after the first blur so users aren't interrupted while typing.
const localError = computed(() => {
    const message = touched.value ? getExternalUrlError(model.value) : undefined
    return message ? t(message) : ''
})
const errorMessage = computed(() => props.error || formField?.error.value || localError.value)
const errorId = useId()

const onUpdate = (value: string | undefined) => {
    model.value = value
    formField?.clear()
}

// WHY: Add a missing protocol on blur (not while typing) so stored links are always absolute.
// Whitespace-only input is cleared so it is saved as empty. Invalid input is left untouched
// so the user can still correct what they typed.
const onBlur = () => {
    touched.value = true
    if (typeof model.value !== 'string') return
    const trimmed = model.value.trim()
    if (!trimmed) {
        if (model.value) model.value = ''
        return
    }
    if (!getExternalUrlError(trimmed)) {
        model.value = normalizeExternalUrl(trimmed)
    }
}
</script>

<template>
    <div class="flex flex-col gap-1" :class="{ 'w-full': fluid }">
        <InputText
            v-bind="$attrs"
            :model-value="model ?? ''"
            inputmode="url"
            autocomplete="url"
            :fluid="fluid"
            :invalid="!!errorMessage"
            :aria-errormessage="errorMessage ? errorId : undefined"
            @update:model-value="onUpdate"
            @blur="onBlur"
        />
        <Message v-if="errorMessage" :id="errorId" severity="error" size="small" variant="simple">
            {{ errorMessage }}
        </Message>
    </div>
</template>
